<script setup lang="ts">
import ImpersonationBanner from "~/components/Pages/Auth/ImpersonationBanner.vue";
import CopilotFloatingButton from "~/components/Copilot/CopilotFloatingButton.vue";
import CopilotPanel from "~/components/Copilot/CopilotPanel.vue";
import AttendanceReviewModal from "~/components/Attendance/review/AttendanceReviewModal.vue";
import TrainingFormLoader from "~/components/Pages/company-catalog/training-modal/TrainingFormLoader.vue";
import { useCopilotPanelStore } from "~/store/copilot/copilot-panel.store";
import { useCopilotAccess } from "~/composables/copilot/useCopilotAccess";
import { useAttendanceReviewStore } from "~/store/attendance/attendance-review.store";
import { useCreateTrainingStore } from "~/store/trainings/create-training.store";
import { hostsTrainingModal } from "~/utils/helpers/trainings/training-modal-host";

const { canUseCopilot } = useCopilotAccess();
const copilotPanelStore = useCopilotPanelStore();
const attendanceReviewStore = useAttendanceReviewStore();
const createTrainingStore = useCreateTrainingStore();
const route = useRoute();

/**
 * The training form, for the pages that do not mount it themselves: the assistant's "Ouvrir la
 * fiche pré-remplie" then opens it over whatever page the admin is on. Async and rendered only
 * while a form is open, so the wizard's chunk is fetched when it is needed and not before.
 */
const TrainingContainer = defineAsyncComponent({
  loader: () => import("~/components/Pages/company-catalog/training-modal/TrainingContainer.vue"),
  loadingComponent: TrainingFormLoader,
  delay: 200,
  // Nuxt's root Suspense has long resolved, so deferring to it would show nothing while the chunk
  // downloads. Opting out is what makes `loadingComponent` and `delay` apply at all.
  suspensible: false,
});

const hostsTrainingForm = computed(
  () => !hostsTrainingModal(route) && (createTrainingStore.showModal || createTrainingStore.showExit),
);

onMounted(async () => {
  await attendanceReviewStore.restoreFromRoute();

  /*
   * A restored run that opened no screen is waiting in the conversation — a picker to answer, or a
   * session to check before the attendance opens. The panel is the only way back to either, and it
   * does not survive a reload by itself.
   */
  if (attendanceReviewStore.hasRunOnScreen && !attendanceReviewStore.isOpen) {
    copilotPanelStore.setIsOpen(true);
  }
});
</script>

<template>
  <ImpersonationBanner />
  <NuxtLayout>
    <NuxtRouteAnnouncer />
    <NuxtPage />
  </NuxtLayout>

  <!-- Mounted once at the root (not per page) so the conversation survives page and layout changes. -->
  <CopilotPanel v-if="canUseCopilot" />

  <!-- The header's trigger, reachable from anywhere: same panel, and draggable out of the way. -->
  <CopilotFloatingButton v-if="canUseCopilot" />

  <!-- Shared by the assistant and the session-page upload, including after a browser reload. -->
  <AttendanceReviewModal v-if="attendanceReviewStore.isOpen" />

  <!-- Same component the catalog pages mount; here it is the host for every other page. -->
  <TrainingContainer v-if="hostsTrainingForm" />
</template>
